import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDynamicTableHeight } from '@/hooks/use-dynamic-table-height';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollableTabsContainer } from '@/components/ui/scrollable-tabs-container';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Activity,
  BarChart3,
  Edit,
  MoreVertical,
  UserCheck,
  UserX,
  ArrowUpDown,
  Settings2,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  LayoutDashboard,
  Landmark,
  PieChart,
  Building2,
  Layers,
  UsersRound,
  Milestone as MilestoneIcon,
  Banknote,
  GitPullRequestArrow,
} from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { BudgetTabContainer, BudgetCard } from '@/components/blocks/budget';
import { CostCentresTable, DEFAULT_COST_CENTRE_COLUMNS, type CostCentre, type CostCentreColumnDefinition } from '@/components/blocks/cost-centres/cost-centres-table';
import { CostCentresColumnConfigurator } from '@/components/blocks/cost-centres/cost-centres-column-configurator';
import { CostCentresHierarchicalTable, type HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';
import { ConfigureStructureModal } from '@/components/blocks/cost-centres/configure-structure-modal';
import { CostCentreDetailsPanel } from '@/components/blocks/cost-centres/cost-centre-details-panel';
import { CreateCostCentrePanel } from '@/components/blocks/cost-centres/create-cost-centre-panel';
import { CostCentreFullModal } from '@/components/blocks/cost-centres/cost-centre-full-modal';
import { formatHours } from '@/lib/budget-utils';
import {
  fetchProjects,
  fetchClients,
  fetchEmployees,
  fetchResourceAllocations,
  fetchProjectDetails,
  fetchCostCentresByProjectId,
  createCostCentre,
  getDataMode,
} from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
  cn,
} from '@/lib/utils';
import { ROUTES } from '@/routes';
import type {
  Project,
  Client,
  Employee,
  TimeEntry,
  Expense,
  Milestone,
  ResourceAllocation,
  ChangeRequest,
} from '@/types';

interface ProjectDetailsPageProps {
  projectId?: string;
  onBack?: () => void;
}

interface TeamMember {
  id: string;
  employeeId: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  allocatedHours: number;
  actualHours: number;
  remaining: number;
  startDate: string;
  endDate: string;
  status: 'Active' | 'On Track' | 'At Risk' | 'Over Allocated' | 'Completed';
  utilizationPercent: number;
}

export function ProjectDetailsPage({ projectId: projectIdProp, onBack }: ProjectDetailsPageProps) {
  // Get projectId from URL params or props (props for backward compatibility)
  const { projectId: projectIdFromUrl } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdProp || projectIdFromUrl || '';

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [projectManager, setProjectManager] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isStructureView, setIsStructureView] = useState(false);
  const [sortField, setSortField] = useState<keyof TeamMember>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { handleApiError } = useErrorHandler({ useToast: true });
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Collapsible KPI cards state (expanded by default)
  const [isKpiOpen, setIsKpiOpen] = useState(true);
  
  // Cost Centres state
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [hierarchicalCostCentres, setHierarchicalCostCentres] = useState<HierarchicalCostCentre[]>([]);
  const [costCentreColumns, setCostCentreColumns] = useState<CostCentreColumnDefinition[]>(DEFAULT_COST_CENTRE_COLUMNS);
  const [costCentreColumnConfigOpen, setCostCentreColumnConfigOpen] = useState(false);
  const [configureStructureOpen, setConfigureStructureOpen] = useState(false);
  const [selectedCostCentre, setSelectedCostCentre] = useState<CostCentre | null>(null);
  const [costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen] = useState(false);
  const [costCentreFullModalOpen, setCostCentreFullModalOpen] = useState(false);
  const [createCostCentrePanelOpen, setCreateCostCentrePanelOpen] = useState(false);

  // Dynamic height for tabs container
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsContainerHeight = useDynamicTableHeight(tabsContainerRef, 24); // 24px bottom padding

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [projectsData, clientsData, employeesData, resourceAllocationsData] = await Promise.all([
          fetchProjects('projects'),
          fetchClients('projects'),
          fetchEmployees('projects'),
          fetchResourceAllocations('projects'),
        ]);

        const currentProject = projectsData.find((p: Project) => p.id === projectId);
        if (currentProject) {
          setProject(currentProject);
          // Get the first client ID from the comma-separated list
          const firstClientId = currentProject.clientIds?.split(',')[0]?.trim();
          setClient(clientsData.find(c => c.id === firstClientId) || null);
          setProjectManager(employeesData.find(e => e.id === currentProject.projectManagerId) || null);
          
          // Generate mock data for this project
          const projectAllocations = resourceAllocationsData.filter(r => r.projectId === projectId);
          setResourceAllocations(projectAllocations);
          
          // Mock time entries
          const mockTimeEntries: TimeEntry[] = projectAllocations.slice(0, 10).map((alloc, i) => ({
            id: `te-${projectId}-${i}`,
            projectId: projectId,
            employeeId: alloc.employeeId,
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
            hours: 6 + Math.random() * 3,
            description: 'Development work',
            billable: Math.random() > 0.2,
            approved: Math.random() > 0.3,
            costRate: 50 + Math.random() * 30,
            billRate: 100 + Math.random() * 60,
          }));
          setTimeEntries(mockTimeEntries);
          
          // Mock expenses
          const mockExpenses: Expense[] = [
            {
              id: `exp-${projectId}-1`,
              projectId: projectId,
              employeeId: projectAllocations[0]?.employeeId || 'e1',
              date: new Date(Date.now() - 5 * 86400000).toISOString().split('T')[0],
              category: 'Travel',
              amount: 450,
              description: 'Client site visit',
              billable: true,
              approved: true,
            },
            {
              id: `exp-${projectId}-2`,
              projectId: projectId,
              employeeId: projectAllocations[1]?.employeeId || 'e2',
              date: new Date(Date.now() - 10 * 86400000).toISOString().split('T')[0],
              category: 'Software',
              amount: 299,
              description: 'Development tools license',
              billable: true,
              approved: false,
            },
          ];
          setExpenses(mockExpenses);
          
          // Mock milestones
          const mockMilestones: Milestone[] = [
            {
              id: `ms-${projectId}-1`,
              projectId: projectId,
              name: 'Project Kickoff',
              dueDate: currentProject.startDate,
              completedDate: currentProject.startDate,
              status: 'completed',
              paymentAmount: currentProject.budgetRevenue * 0.2,
              paymentReceived: true,
            },
            {
              id: `ms-${projectId}-2`,
              projectId: projectId,
              name: 'Phase 1 Delivery',
              dueDate: new Date(new Date(currentProject.startDate).getTime() + 60 * 86400000).toISOString().split('T')[0],
              completedDate: new Date(new Date(currentProject.startDate).getTime() + 58 * 86400000).toISOString().split('T')[0],
              status: 'completed',
              paymentAmount: currentProject.budgetRevenue * 0.3,
              paymentReceived: true,
            },
            {
              id: `ms-${projectId}-3`,
              projectId: projectId,
              name: 'Phase 2 Delivery',
              dueDate: new Date(new Date(currentProject.startDate).getTime() + 120 * 86400000).toISOString().split('T')[0],
              status: currentProject.completionPercent > 70 ? 'completed' : 'pending',
              completedDate: currentProject.completionPercent > 70 ? new Date(new Date(currentProject.startDate).getTime() + 118 * 86400000).toISOString().split('T')[0] : undefined,
              paymentAmount: currentProject.budgetRevenue * 0.3,
              paymentReceived: false,
            },
            {
              id: `ms-${projectId}-4`,
              projectId: projectId,
              name: 'Final Delivery',
              dueDate: currentProject.endDate,
              status: currentProject.status === 'completed' ? 'completed' : 'pending',
              completedDate: currentProject.status === 'completed' ? currentProject.endDate : undefined,
              paymentAmount: currentProject.budgetRevenue * 0.2,
              paymentReceived: false,
            },
          ];
          setMilestones(mockMilestones);
          
          // Mock change requests
          const mockChangeRequests: ChangeRequest[] = [
            {
              id: `cr-${projectId}-1`,
              projectId: projectId,
              title: 'Additional feature request',
              description: 'Client requested additional reporting functionality',
              requestedBy: 'Client',
              requestedDate: new Date(Date.now() - 15 * 86400000).toISOString().split('T')[0],
              status: 'approved',
              impactBudget: 15000,
              impactSchedule: 10,
              approvedBy: currentProject.projectManagerId,
              approvedDate: new Date(Date.now() - 10 * 86400000).toISOString().split('T')[0],
            },
            {
              id: `cr-${projectId}-2`,
              projectId: projectId,
              title: 'Scope clarification',
              description: 'Clarification on integration requirements',
              requestedBy: 'Project Team',
              requestedDate: new Date(Date.now() - 5 * 86400000).toISOString().split('T')[0],
              status: 'pending',
              impactBudget: 0,
              impactSchedule: 0,
            },
          ];
          setChangeRequests(mockChangeRequests);
          
          // Generate comprehensive team member data
          const mockTeamMembers: TeamMember[] = projectAllocations.map((alloc, index) => {
            const employee = employeesData.find(e => e.id === alloc.employeeId);
            const allocatedHours = Math.floor(Math.random() * 200) + 100; // 100-300 hours
            const actualHours = Math.floor(allocatedHours * (0.3 + Math.random() * 0.8)); // 30-110% of allocated
            const remaining = allocatedHours - actualHours;
            const utilizationPercent = (actualHours / allocatedHours) * 100;
            
            // Determine status based on utilization and dates
            let status: TeamMember['status'];
            const now = new Date();
            const endDate = new Date(alloc.endDate);
            
            if (endDate < now) {
              status = 'Completed';
            } else if (utilizationPercent > 100) {
              status = 'Over Allocated';
            } else if (utilizationPercent > 85) {
              status = 'At Risk';
            } else if (utilizationPercent > 50) {
              status = 'On Track';
            } else {
              status = 'Active';
            }
            
            // Generate avatar URL using UI Avatars service
            const name = employee?.name || 'Unknown';
            const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

            return {
              id: alloc.id,
              employeeId: alloc.employeeId,
              name: name,
              email: employee?.email || '',
              role: alloc.role,
              avatar: avatarUrl,
              allocatedHours,
              actualHours,
              remaining,
              startDate: alloc.startDate,
              endDate: alloc.endDate,
              status,
              utilizationPercent,
            };
          });
          
          setTeamMembers(mockTeamMembers);
          
          // Fetch cost centres - use API in live mode, mock data in demo mode
          const dataMode = getDataMode();
          if (dataMode === 'live') {
            try {
              const apiCostCentres = await fetchCostCentresByProjectId(projectId);
              setCostCentres(apiCostCentres);
            } catch (error) {
              console.error('Failed to fetch cost centres from API:', error);
              setCostCentres([]);
            }
          } else {
            // Helper arrays for variety (used by both flat and hierarchical views)
            const types = ['Functional Area', 'Administrative', 'Technical Support', 'Operations', 'Support Services'];
            const statuses: Array<'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled'> = ['Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
            const departments = ['Engineering', 'Design', 'Quality Assurance', 'PMO', 'IT Operations', 'Marketing', 'Sales', 'Finance', 'HR', 'Legal'];
            const locations = ['London Office', 'Manchester Office', 'Birmingham Office', 'Head Office', 'Data Centre', 'Edinburgh Office', 'Bristol Office', 'Leeds Office', 'Remote', 'Client Site'];
            const priorities = ['High', 'Medium', 'Low'];
            const owners = ['John Smith', 'Sarah Johnson', 'Mike Davis', 'Emily Brown', 'David Wilson', 'Alice Chen', 'Bob Martinez', 'Carol White', 'Dan Brown', 'Emma Wilson'];
            
            // Mock cost centres data for demo mode - 50 records for scrolling test
            const mockCostCentres: CostCentre[] = [];
            
            // Generate 50 cost centres
            for (let i = 1; i <= 50; i++) {
              const code = `CC-${String(i).padStart(3, '0')}`;
              const typeIndex = (i - 1) % types.length;
              const statusIndex = (i - 1) % statuses.length;
              const deptIndex = (i - 1) % departments.length;
              const locIndex = (i - 1) % locations.length;
              const priorityIndex = (i - 1) % priorities.length;
              const ownerIndex = (i - 1) % owners.length;
              
              // Vary the budget allocation
              const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
              const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3); // 85% to 115% of budget
              const committedFactor = budgetFactor * 0.1;
              
              mockCostCentres.push({
                id: `cc-${projectId}-${i}`,
                code: code,
                name: `Cost Centre ${i}`,
                description: `Description for cost centre ${code}`,
                type: types[typeIndex],
                status: statuses[statusIndex],
                billable: i % 3 !== 0, // 2/3 are billable
                costsBudget: currentProject.budgetCost * budgetFactor,
                actualCosts: currentProject.actualCost * actualFactor,
                committedCosts: currentProject.budgetCost * committedFactor,
                revenueBudget: currentProject.budgetRevenue * budgetFactor,
                actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                hoursBudget: Math.round(budgetFactor * 10000),
                actualHours: Math.round(actualFactor * 9500),
                owner: owners[ownerIndex],
                lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                createdAt: new Date(currentProject.startDate),
                updatedAt: new Date(Date.now() - (i % 10) * 86400000),
                customFields: {
                  'Department': departments[deptIndex],
                  'Location': locations[locIndex],
                  'Priority': priorities[priorityIndex],
                  'Start Date': new Date(currentProject.startDate),
                  'End Date': new Date(currentProject.endDate),
                  'Requires Approval': i % 2 === 0,
                  'Approval Threshold': 1000 + (i * 100),
                  'Cost Code': `${code}-${departments[deptIndex].substring(0, 3).toUpperCase()}`,
                  'GL Account': `${6100 + (i % 5) * 100}-${1000 + (i % 10) * 100}`,
                  'Project Phase': `Phase ${(i % 5) + 1}`,
                },
              });
            }
            setCostCentres(mockCostCentres);
            
            // Mock hierarchical cost centres data - includes all 50 cost centre codes
            const mockHierarchicalCostCentres: HierarchicalCostCentre[] = [];
            
            // Create 5 parent departments, each with 10 cost centres
            const parentDepartments: Array<{ code: string; name: string; range: [number, number] }> = [
              { code: 'CC-DEPT-01', name: 'Engineering Department', range: [1, 10] },
              { code: 'CC-DEPT-02', name: 'Design & Creative', range: [11, 20] },
              { code: 'CC-DEPT-03', name: 'Operations', range: [21, 30] },
              { code: 'CC-DEPT-04', name: 'Support Services', range: [31, 40] },
              { code: 'CC-DEPT-05', name: 'Administration', range: [41, 50] },
            ];
            
            parentDepartments.forEach((dept, deptIndex) => {
              // Calculate totals for parent
              const deptBudgetFactor = 0.2; // Each department gets 20% of total budget
            
              // Add parent department
              mockHierarchicalCostCentres.push({
                id: `cc-${projectId}-dept-${deptIndex + 1}`,
                code: dept.code,
                name: dept.name,
                billable: true,
                costsBudget: currentProject.budgetCost * deptBudgetFactor,
                actualCosts: currentProject.actualCost * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                committedCosts: currentProject.budgetCost * deptBudgetFactor * 0.05,
                revenueBudget: currentProject.budgetRevenue * deptBudgetFactor,
                actualRevenue: currentProject.actualRevenue * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                hoursBudget: Math.round(deptBudgetFactor * 10000),
                actualHours: Math.round(deptBudgetFactor * 9500 * (0.9 + Math.random() * 0.2)),
                parentId: null,
              });
              
              // Add child cost centres (CC-001 to CC-050)
              for (let i = dept.range[0]; i <= dept.range[1]; i++) {
                const code = `CC-${String(i).padStart(3, '0')}`;
                const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
                const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3);
                const committedFactor = budgetFactor * 0.1;
                
                const statusIndex = (i - 1) % statuses.length;
                const ownerIndex = (i - 1) % owners.length;
                
                mockHierarchicalCostCentres.push({
                  id: `cc-${projectId}-${i}`,
                  code: code,
                  name: `Cost Centre ${i}`,
                  type: types[(i - 1) % types.length],
                  status: statuses[statusIndex],
                  billable: i % 3 !== 0,
                  costsBudget: currentProject.budgetCost * budgetFactor,
                  actualCosts: currentProject.actualCost * actualFactor,
                  committedCosts: currentProject.budgetCost * committedFactor,
                  revenueBudget: currentProject.budgetRevenue * budgetFactor,
                  actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                  hoursBudget: Math.round(budgetFactor * 10000),
                  actualHours: Math.round(actualFactor * 9500),
                  owner: owners[ownerIndex],
                  lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                  parentId: `cc-${projectId}-dept-${deptIndex + 1}`,
                });
              }
            });
            
            setHierarchicalCostCentres(mockHierarchicalCostCentres);
          }
        }

        setEmployees(employeesData);
      } catch (error) {
        handleApiError(error, `/api/projects/${projectId}`, 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [projectId, handleApiError]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton for Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton for Tabs */}
        <Card>
          <CardHeader>
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <div className="pt-4">
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="text-muted-foreground">Project not found</div>
        <Button onClick={() => onBack ? onBack() : navigate(ROUTES.PROJECTS)}>
          Back to Projects
        </Button>
      </div>
    );
  }

  const getEmployeeName = (employeeId: string) => {
    return employees.find(e => e.id === employeeId)?.name || 'Unknown';
  };
  
  const getStatusBadgeVariant = (status: TeamMember['status']): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'Completed':
        return 'secondary';
      case 'On Track':
        return 'default';
      case 'Active':
        return 'outline';
      case 'At Risk':
        return 'destructive';
      case 'Over Allocated':
        return 'destructive';
      default:
        return 'default';
    }
  };
  
  const getStatusColor = (status: TeamMember['status'] | string): string => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'completed':
        return 'text-gray-600';
      case 'on track':
      case 'on-track':
        return 'text-green-600';
      case 'active':
      case 'in-progress':
        return 'text-blue-600';
      case 'at risk':
      case 'at-risk':
        return 'text-amber-600';
      case 'over allocated':
      case 'over-allocated':
        return 'text-red-600';
      case 'planning':
      case 'on-hold':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  };
  
  const handleSort = (field: keyof TeamMember) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const sortedTeamMembers = [...teamMembers].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    }
    
    return 0;
  });
  
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const totalBillableHours = timeEntries
    .filter(t => t.billable)
    .reduce((sum, t) => sum + t.hours, 0);

  const totalNonBillableHours = timeEntries
    .filter(t => !t.billable)
    .reduce((sum, t) => sum + t.hours, 0);

  const totalExpenseAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

  const completedMilestones = milestones.filter(m => m.status === 'completed').length;
  const overdueMilestones = milestones.filter(m => m.status === 'overdue').length;

  const activeResources = resourceAllocations.filter(r => r.status === 'confirmed').length;

  const pendingChangeRequests = changeRequests.filter(cr => cr.status === 'pending').length;
  const approvedChangeRequests = changeRequests.filter(cr => cr.status === 'approved').length;

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <TooltipProvider>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="border-b bg-muted/30">
            <TabsList variant="icon" showArrows>
              <TabsTrigger variant="icon" value="overview" showUnderline>
                <LayoutDashboard className="size-5" />
                Overview
              </TabsTrigger>
              <TabsTrigger variant="icon" value="financials" showUnderline>
                <Landmark className="size-5" />
                Financials
              </TabsTrigger>
              <TabsTrigger variant="icon" value="budget" showUnderline>
                <PieChart className="size-5" />
                Budget
              </TabsTrigger>
              <TabsTrigger variant="icon" value="cost-centres" showUnderline>
                <Layers className="size-5" />
                Cost Centres
              </TabsTrigger>
              <TabsTrigger variant="icon" value="team" showUnderline>
                <UsersRound className="size-5" />
                Team
              </TabsTrigger>
              <TabsTrigger variant="icon" value="milestones" showUnderline>
                <MilestoneIcon className="size-5" />
                Milestones
              </TabsTrigger>
              <TabsTrigger variant="icon" value="time" showUnderline>
                <Banknote className="size-5" />
                Time & Expenses
              </TabsTrigger>
              <TabsTrigger variant="icon" value="changes" showUnderline>
                <GitPullRequestArrow className="size-5" />
                Change Requests
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Scrollable Tab Content Container */}
          <div ref={tabsContainerRef}>
            <ScrollableTabsContainer height={`${tabsContainerHeight}px`}>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
          {/* Key Metrics - Collapsible */}
          <Collapsible open={isKpiOpen} onOpenChange={setIsKpiOpen}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Key Metrics</h2>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-9 p-0">
                  {isKpiOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle KPI cards</span>
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue Budget</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Actual: <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Cost Budget: <SensitiveData>{formatCurrency(project.budgetLabour + project.budgetExpense)}</SensitiveData>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Cost Variance</CardTitle>
                {project.variancePercent < 0 ? (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                ) : (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                )}
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getVarianceColor(project.variancePercent)}`}>
                  {project.variancePercent > 0 ? '+' : ''}
                  {formatPercent(project.variancePercent)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  <SensitiveData>{formatCurrency(Math.abs(project.variance))}</SensitiveData>{' '}
                  {project.variance < 0 ? 'over budget' : 'under budget'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Margin</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Profit: <SensitiveData>{formatCurrency(project.actualRevenue - project.actualLabour - project.actualExpense)}</SensitiveData>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Hours Budget</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{project.budgetHours.toLocaleString()} hrs</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Actual: {project.actualHours.toLocaleString()} hrs
                </p>
                <p className={`text-xs mt-1 ${getVarianceColor(project.hoursVariancePercent)}`}>
                  {project.hoursVariance > 0 ? '+' : ''}{project.hoursVariance.toLocaleString()} hrs ({project.hoursVariancePercent > 0 ? '+' : ''}{formatPercent(project.hoursVariancePercent)})
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completion</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercent(project.completionPercent, 0)}</div>
                <Progress value={project.completionPercent} className="mt-2" />
              </CardContent>
            </Card>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Project Information */}
            <Card>
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Project Type</span>
                  <span className="font-medium capitalize">{project.type.replace('-', ' ')}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revenue Recognition</span>
                  <span className="font-medium capitalize">
                    {project.revenueRecognitionMethod.replace('-', ' ')}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Date</span>
                  <span className="font-medium">{formatDate(project.startDate)}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">End Date</span>
                  <span className="font-medium">{formatDate(project.endDate)}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Project Manager</span>
                  <span className="font-medium">{projectManager?.name || 'Unknown'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Client Information */}
            <Card>
              <CardHeader>
                <CardTitle>Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client Name</span>
                  <span className="font-medium">{client?.name || 'Unknown'}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Industry</span>
                  <span className="font-medium">{client?.industry || 'Unknown'}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contact</span>
                  <span className="font-medium">{client?.contactName || 'Unknown'}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium text-sm">{client?.contactEmail || 'Unknown'}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Active Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeResources}</div>
                <p className="text-xs text-muted-foreground">Team members</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Milestones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {completedMilestones}/{milestones.length}
                </div>
                <p className="text-xs text-muted-foreground">
                  {overdueMilestones > 0 && `${overdueMilestones} overdue`}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Change Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{changeRequests.length}</div>
                <p className="text-xs text-muted-foreground">
                  {pendingChangeRequests} pending, {approvedChangeRequests} approved
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">WIP Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground">
                  Unbilled: <SensitiveData>{formatCurrency(project.unbilledAmount)}</SensitiveData>
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Budget vs Actual</CardTitle>
                <CardDescription>Labour, expenses, and revenue breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Budget</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Labour</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.budgetLabour)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.actualLabour)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={getVarianceColor((project.actualLabour - project.budgetLabour) / project.budgetLabour * 100)}>
                          <SensitiveData>
                            {formatCurrency(project.actualLabour - project.budgetLabour)}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Expenses</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.budgetExpense)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.actualExpense)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={getVarianceColor((project.actualExpense - project.budgetExpense) / project.budgetExpense * 100)}>
                          <SensitiveData>
                            {formatCurrency(project.actualExpense - project.budgetExpense)}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Revenue</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={getVarianceColor((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue * 100)}>
                          <SensitiveData>
                            {formatCurrency(project.actualRevenue - project.budgetRevenue)}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Financial Summary</CardTitle>
                <CardDescription>Key financial metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revenue Budget</span>
                  <span className="font-medium">
                    <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cost Budget</span>
                  <span className="font-medium">
                    <SensitiveData>{formatCurrency(project.budgetLabour + project.budgetExpense)}</SensitiveData>
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Actual Costs</span>
                  <span className="font-medium">
                    <SensitiveData>{formatCurrency(project.totalActual)}</SensitiveData>
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cost Variance</span>
                  <span className={`font-medium ${getVarianceColor(project.variancePercent)}`}>
                    <SensitiveData>{formatCurrency(project.variance)}</SensitiveData>
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Margin %</span>
                  <span className="font-medium">
                    <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">WIP Amount</span>
                  <span className="font-medium">
                    <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unbilled Amount</span>
                  <span className="font-medium">
                    <SensitiveData>{formatCurrency(project.unbilledAmount)}</SensitiveData>
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-4">
          <BudgetTabContainer
            projectId={projectId}
            projectType={project.type}
            canEdit={true}
            onEdit={() => {
              // TODO: Implement edit budget modal
              console.log('Edit budget clicked');
            }}
            onViewHistory={() => {
              // TODO: Implement view history modal
              console.log('View history clicked');
            }}
            onExport={() => {
              // TODO: Implement export functionality
              console.log('Export clicked');
            }}
          />
        </TabsContent>

        {/* Cost Centres Tab */}
        <TabsContent value="cost-centres" className="space-y-4">
          {/* Header Section */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCostCentreColumnConfigOpen(true)}
                className="gap-2"
              >
                <Settings2 className="h-4 w-4" />
                Configure Columns
              </Button>
              {!isStructureView && (
                <Button
                  size="sm"
                  onClick={() => setCreateCostCentrePanelOpen(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New
                </Button>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="relative px-3"
                      aria-label="Recycle Bin"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-medium bg-muted text-muted-foreground rounded-full border">
                        0
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Recycle Bin</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isStructureView && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfigureStructureOpen(true)}
                  className="gap-2"
                >
                  <Settings2 className="h-4 w-4" />
                  Configure Structure
                </Button>
              )}
              <div className="flex items-center gap-2">
                <label htmlFor="structure-view-toggle" className="text-sm font-medium cursor-pointer">
                  Structure View
                </label>
                <Switch
                  id="structure-view-toggle"
                  checked={isStructureView}
                  onCheckedChange={setIsStructureView}
                />
              </div>
            </div>
          </div>

          {/* Content Display Area */}
          {isStructureView ? (
            // Structure View (Tree/Hierarchy)
            <CostCentresHierarchicalTable 
              data={hierarchicalCostCentres} 
              columns={costCentreColumns}
              useCardWrapper={false}
              onRowClick={(costCentre) => {
                setSelectedCostCentre(costCentre);
                setCostCentreDetailsPanelOpen(true);
              }}
            />
          ) : (
            // List/Table View
            <CostCentresTable 
              data={costCentres} 
              columns={costCentreColumns}
              useCardWrapper={false}
              onRowClick={(costCentre) => {
                setSelectedCostCentre(costCentre);
                setCostCentreDetailsPanelOpen(true);
              }}
            />
          )}
        </TabsContent>

        {/* Column Configurator Modal */}
        <CostCentresColumnConfigurator
          open={costCentreColumnConfigOpen}
          onOpenChange={setCostCentreColumnConfigOpen}
          columns={costCentreColumns}
          onSave={(newColumns) => {
            setCostCentreColumns(newColumns);
            // In a real app, save to user preferences/backend
          }}
        />

        {/* Create Cost Centre Panel */}
        <CreateCostCentrePanel
          open={createCostCentrePanelOpen}
          onOpenChange={setCreateCostCentrePanelOpen}
          existingCodes={[...costCentres.map(cc => cc.code), ...hierarchicalCostCentres.map(cc => cc.code)]}
          onSave={async (newCostCentre) => {
            const mode = getDataMode();
            
            if (mode === 'live' && projectId) {
              // Live mode - call the API to persist the cost centre
              try {
                const createdCostCentre = await createCostCentre({
                  projectId: projectId,
                  code: newCostCentre.code,
                  name: newCostCentre.name,
                  type: newCostCentre.type,
                  status: newCostCentre.status,
                  billable: newCostCentre.billable,
                  owner: newCostCentre.owner,
                  description: newCostCentre.description,
                  costsBudget: newCostCentre.costsBudget,
                  revenueBudget: newCostCentre.revenueBudget,
                  hoursBudget: newCostCentre.hoursBudget,
                });
                
                // Use the ID from the API response
                const costCentreWithId: CostCentre = {
                  ...newCostCentre,
                  id: createdCostCentre.id || `cc-${Date.now()}`,
                };
                
                // Add to both flat and hierarchical lists
                setCostCentres(prev => [...prev, costCentreWithId]);
                setHierarchicalCostCentres(prev => [...prev, { ...costCentreWithId, children: [] }]);
                
                console.log('Created new cost centre via API:', costCentreWithId);
              } catch (error) {
                console.error('Failed to create cost centre:', error);
                // Still add to local state for demo purposes, but log the error
                const id = `cc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const costCentreWithId: CostCentre = {
                  ...newCostCentre,
                  id,
                };
                setCostCentres(prev => [...prev, costCentreWithId]);
                setHierarchicalCostCentres(prev => [...prev, { ...costCentreWithId, children: [] }]);
              }
            } else {
              // Demo mode - just update local state
              const id = `cc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              const costCentreWithId: CostCentre = {
                ...newCostCentre,
                id,
              };
              
              // Add to both flat and hierarchical lists
              setCostCentres(prev => [...prev, costCentreWithId]);
              setHierarchicalCostCentres(prev => [...prev, { ...costCentreWithId, children: [] }]);
              
              console.log('Created new cost centre (demo mode):', costCentreWithId);
            }
          }}
        />

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4">
          {/* Team Metrics Summary Cards */}
          <div className="grid gap-3 md:grid-cols-5">
            {(() => {
              const memberCount = teamMembers.length;
              const memberColor = memberCount >= 5 ? 'green' : memberCount >= 3 ? 'amber' : 'red';
              const borderClass = `border-l-${memberColor}-500`;
              const bgClass = `bg-${memberColor}-50/50`;
              
              return (
                <Card className={`border-l-4 ${borderClass} ${bgClass}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Team Members
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{memberCount}</div>
                  </CardContent>
                </Card>
              );
            })()}

            {(() => {
              const totalAllocated = teamMembers.reduce((sum, m) => sum + m.allocatedHours, 0);
              const borderClass = 'border-l-blue-500';
              const bgClass = 'bg-blue-50/50';
              
              return (
                <Card className={`border-l-4 ${borderClass} ${bgClass}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Allocated Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatHours(totalAllocated)}</div>
                  </CardContent>
                </Card>
              );
            })()}

            {(() => {
              const totalActual = teamMembers.reduce((sum, m) => sum + m.actualHours, 0);
              const totalAllocated = teamMembers.reduce((sum, m) => sum + m.allocatedHours, 0);
              const utilizationPercent = totalAllocated > 0 ? (totalActual / totalAllocated) * 100 : 0;
              const hoursColor = utilizationPercent <= 85 ? 'green' : utilizationPercent <= 100 ? 'amber' : 'red';
              const borderClass = `border-l-${hoursColor}-500`;
              const bgClass = `bg-${hoursColor}-50/50`;
              
              return (
                <Card className={`border-l-4 ${borderClass} ${bgClass}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Actual Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatHours(totalActual)}</div>
                  </CardContent>
                </Card>
              );
            })()}

            {(() => {
              const totalRemaining = teamMembers.reduce((sum, m) => sum + m.remaining, 0);
              const remainingColor = totalRemaining > 0 ? 'green' : totalRemaining < -50 ? 'red' : 'amber';
              const borderClass = `border-l-${remainingColor}-500`;
              const bgClass = `bg-${remainingColor}-50/50`;
              
              return (
                <Card className={`border-l-4 ${borderClass} ${bgClass}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Remaining Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatHours(totalRemaining)}</div>
                  </CardContent>
                </Card>
              );
            })()}

            {(() => {
              const atRiskCount = teamMembers.filter(m => 
                m.status === 'At Risk' || m.status === 'Over Allocated'
              ).length;
              const riskColor = atRiskCount === 0 ? 'green' : atRiskCount <= 2 ? 'amber' : 'red';
              const borderClass = `border-l-${riskColor}-500`;
              const bgClass = `bg-${riskColor}-50/50`;
              
              return (
                <Card className={`border-l-4 ${borderClass} ${bgClass}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Members At Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{atRiskCount}</div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          {/* Team Management Table */}
          <Card>
            <CardHeader>
              <CardTitle>Team Management</CardTitle>
              <CardDescription>Comprehensive team member tracking and allocation</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('role')}
                      >
                        Role
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-mr-3 h-8"
                        onClick={() => handleSort('allocatedHours')}
                      >
                        Allocated Hours
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-mr-3 h-8"
                        onClick={() => handleSort('actualHours')}
                      >
                        Actual Hours
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-mr-3 h-8"
                        onClick={() => handleSort('remaining')}
                      >
                        Remaining
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('startDate')}
                      >
                        Start Date
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('endDate')}
                      >
                        End Date
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTeamMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground h-32">
                        No team members found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedTeamMembers.map(member => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <Avatar className="h-8 w-12 rounded-md">
                            <AvatarImage src={member.avatar} alt={member.name} className="object-cover" />
                            <AvatarFallback className="text-xs rounded-md">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>
                        <TableCell className="font-medium">
                          {member.name}
                        </TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell className="text-right font-mono">
                          {member.allocatedHours.toFixed(0)}h
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {member.actualHours.toFixed(0)}h
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <span className={member.remaining < 0 ? 'text-red-600 font-semibold' : ''}>
                            {member.remaining > 0 ? '+' : ''}{member.remaining.toFixed(0)}h
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(member.startDate)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(member.endDate)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(member.status)}>
                            {member.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Milestones Tab */}
        <TabsContent value="milestones" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Project Milestones</CardTitle>
              <CardDescription>Key deliverables and payment milestones</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Milestone</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Payment</TableHead>
                    <TableHead>Paid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {milestones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No milestones defined
                      </TableCell>
                    </TableRow>
                  ) : (
                    milestones.map(milestone => (
                      <TableRow key={milestone.id}>
                        <TableCell className="font-medium">{milestone.name}</TableCell>
                        <TableCell>{formatDate(milestone.dueDate)}</TableCell>
                        <TableCell>
                          {milestone.completedDate ? formatDate(milestone.completedDate) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              milestone.status === 'completed'
                                ? 'default'
                                : milestone.status === 'overdue'
                                ? 'destructive'
                                : 'outline'
                            }
                          >
                            {milestone.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {milestone.paymentAmount ? (
                            <SensitiveData>{formatCurrency(milestone.paymentAmount)}</SensitiveData>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {milestone.paymentReceived ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time & Expenses Tab */}
        <TabsContent value="time" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Billable Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalBillableHours.toFixed(1)}</div>
                <p className="text-xs text-muted-foreground">hours logged</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Non-Billable Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalNonBillableHours.toFixed(1)}</div>
                <p className="text-xs text-muted-foreground">hours logged</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(totalExpenseAmount)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground">{expenses.length} expenses</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent Time Entries</CardTitle>
              <CardDescription>Last 10 time entries for this project</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead>Billable</TableHead>
                    <TableHead>Approved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeEntries.slice(0, 10).map(entry => (
                    <TableRow key={entry.id}>
                      <TableCell>{formatDate(entry.date)}</TableCell>
                      <TableCell>{getEmployeeName(entry.employeeId)}</TableCell>
                      <TableCell className="max-w-xs truncate">{entry.description}</TableCell>
                      <TableCell className="text-right">{entry.hours.toFixed(1)}</TableCell>
                      <TableCell>
                        {entry.billable ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.approved ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Clock className="h-4 w-4 text-yellow-500" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Expenses</CardTitle>
              <CardDescription>Last 10 expenses for this project</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Billable</TableHead>
                    <TableHead>Approved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.slice(0, 10).map(expense => (
                    <TableRow key={expense.id}>
                      <TableCell>{formatDate(expense.date)}</TableCell>
                      <TableCell>{getEmployeeName(expense.employeeId)}</TableCell>
                      <TableCell>{expense.category}</TableCell>
                      <TableCell className="max-w-xs truncate">{expense.description}</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(expense.amount)}</SensitiveData>
                      </TableCell>
                      <TableCell>
                        {expense.billable ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {expense.approved ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Clock className="h-4 w-4 text-yellow-500" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Change Requests Tab */}
        <TabsContent value="changes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Change Requests</CardTitle>
              <CardDescription>Scope changes and their impact on budget and schedule</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Budget Impact</TableHead>
                    <TableHead className="text-right">Schedule Impact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {changeRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No change requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    changeRequests.map(cr => (
                      <TableRow key={cr.id}>
                        <TableCell className="font-medium">{cr.title}</TableCell>
                        <TableCell>{cr.requestedBy}</TableCell>
                        <TableCell>{formatDate(cr.requestedDate)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              cr.status === 'approved'
                                ? 'default'
                                : cr.status === 'rejected'
                                ? 'destructive'
                                : 'outline'
                            }
                          >
                            {cr.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <SensitiveData>{formatCurrency(cr.impactBudget)}</SensitiveData>
                        </TableCell>
                        <TableCell className="text-right">
                          {cr.impactSchedule > 0 ? `+${cr.impactSchedule}` : cr.impactSchedule} days
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
            </ScrollableTabsContainer>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Configure Structure Modal */}
      <ConfigureStructureModal
        open={configureStructureOpen}
        onOpenChange={setConfigureStructureOpen}
        projectName={project?.name || ''}
        costCentres={hierarchicalCostCentres}
        onSave={async (updatedStructure) => {
          // TODO: Implement API call to save structure
          console.log('Saving structure:', updatedStructure);
          setHierarchicalCostCentres(updatedStructure);
          // Show success toast
        }}
      />

      {/* Cost Centre Details Panel */}
      <CostCentreDetailsPanel
        costCentre={selectedCostCentre}
        open={costCentreDetailsPanelOpen}
        onOpenChange={setCostCentreDetailsPanelOpen}
        onEdit={(costCentre) => {
          // TODO: Open edit modal with cost centre data
          console.log('Edit cost centre:', costCentre);
          // In a real app, this would open the CreateNewCostCentreModal in edit mode
        }}
        onDelete={(costCentreId) => {
          // Delete the cost centre
          setCostCentres(prev => prev.filter(cc => cc.id !== costCentreId));
          setHierarchicalCostCentres(prev => prev.filter(cc => cc.id !== costCentreId));
          console.log('Deleted cost centre:', costCentreId);
          // In a real app, this would call an API to delete the cost centre
        }}
        onMarkInactive={(costCentreId) => {
          // Mark the cost centre as inactive
          console.log('Mark inactive cost centre:', costCentreId);
          // In a real app, this would call an API to update the cost centre status
        }}
        onExpandToFullView={() => {
          // Close the side drawer and open the full modal
          setCostCentreDetailsPanelOpen(false);
          setCostCentreFullModalOpen(true);
        }}
      />

      {/* Cost Centre Full Modal */}
      <CostCentreFullModal
        costCentre={selectedCostCentre}
        allCostCentres={costCentres}
        open={costCentreFullModalOpen}
        onOpenChange={setCostCentreFullModalOpen}
        onCostCentreChange={(costCentre) => {
          setSelectedCostCentre(costCentre);
        }}
        onMinimize={() => {
          setCostCentreFullModalOpen(false);
          setCostCentreDetailsPanelOpen(true);
        }}
      />
    </div>
  );
}
